import { getBillingPlansPricingUrl } from '@/components/constants/billing-urls';
import { useTrialEndPlanSelectionModal } from '@/components/modal/trial-end-plan-selection-modal/useTrialEndPlanSelectionModal';
import {
  getNewProductTierProduct,
  useQueryProducts,
} from '@/hooks/query/useQueryProducts';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useQueryUserBillingInfo } from '@/hooks/query/useQueryUserBillingInfo';
import { useFeatureLists } from '@/hooks/useFeatureLists';
import { useTranslations } from '@/utils/translations';
import { AppleIapInfoText } from '@heidi/commerce';
import { type Period } from '@heidi/commerce/contracts';
import { SubscriptionStatus, SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useCombinedQueriesTeamGeneralInfo } from '@heidi/workspace/data';
import { type CSSProperties } from 'react';
import { PricingPlanCard } from './pricing-plan-card';
export interface PricingPlanCardsProps {
  period: Period;
}

export function PricingPlanCards({ period }: PricingPlanCardsProps) {
  const { data: previousStatusBeforeClinicianTrial } = useQueryUserBillingInfo<
    SubscriptionStatus | null | undefined
  >((billingInfo) => billingInfo?.previous_status_before_clinician_trial ?? undefined);
  const { t, locale } = useTranslations();
  const { data: isNewUserAfterEvidenceLaunch } = useQueryUser(
    (user) => user?.is_new_user_after_evidence_launch
  );

  const {
    visiblePlans,
    handleClinicianCheckout,
    handlePracticeCheckout,
    isPending,
    currentPlan,
    handleDowngradeToFree,
    handleClose,
    isAppleCompliant,
  } = useTrialEndPlanSelectionModal(period);

  const { data: products } = useQueryProducts({ heidi2Catalogue: true });

  const clinicianProduct = getNewProductTierProduct(
    SubscriptionTier.CLINICIAN_PRO,
    products
  );
  const practiceProduct = getNewProductTierProduct(
    SubscriptionTier.CLINICIAN_PRO_PLUS,
    products
  );

  const {
    freeFeatures,
    scribeProFeatures,
    practiceOldFeatures,
    clinicianFeatures,
    clinicianEvidenceFeatures,
    practiceFeatures,
    practiceNewFeatures,
  } = useFeatureLists();

  const { hasTeam, isTeamStripeManaged } = useCombinedQueriesTeamGeneralInfo();

  const shouldDisableUpgradeForNonStripeManagedTeam = hasTeam && !isTeamStripeManaged;
  const contactSalesTooltip = shouldDisableUpgradeForNonStripeManagedTeam
    ? t('upgrade.contactSalesToUpgrade')
    : undefined;
  const pricingPlansUrl = getBillingPlansPricingUrl(locale);
  const planColumnCount = visiblePlans.length;

  return (
    <div
      className="grid w-full min-w-0 grid-cols-1 gap-4 md:items-stretch md:[grid-template-columns:repeat(var(--pricing-plan-column-count),minmax(0,1fr))]"
      style={
        {
          '--pricing-plan-column-count': planColumnCount,
        } as CSSProperties
      }
    >
      {visiblePlans.includes(SubscriptionTier.FREE) && (
        <PricingPlanCard
          tier={SubscriptionTier.FREE}
          period={period}
          // FREE has no catalogue row; borrow clinician (else practice) only to format 0 in the user's currency ($0 when neither is loaded).
          product={clinicianProduct ?? practiceProduct}
          features={freeFeatures}
          featuresSectionTitle={'pricingPlansModal.features.includes'}
          ctaText={
            isNewUserAfterEvidenceLaunch
              ? 'pricingPlansModal.returnToFree'
              : 'pricingPlansModal.downgradeToFree'
          }
          onCtaClick={handleDowngradeToFree}
          seeMoreHref={pricingPlansUrl}
        />
      )}
      {visiblePlans.includes(SubscriptionTier.PRO) && (
        <PricingPlanCard
          tier={SubscriptionTier.PRO}
          period={period}
          features={scribeProFeatures}
          featuresSectionTitle={'pricingPlansModal.features.includes'}
          ctaText="pricingPlansModal.returnToPro"
          onCtaClick={handleClose}
        />
      )}
      {visiblePlans.includes(SubscriptionTier.PRO_PLUS) && (
        <PricingPlanCard
          tier={SubscriptionTier.PRO_PLUS}
          period={period}
          features={practiceOldFeatures}
          featuresSectionTitle={'pricingPlansModal.features.includes'}
          ctaText="pricingPlansModal.returnToOldPlan"
          onCtaClick={handleClose}
        />
      )}
      {visiblePlans.includes(SubscriptionTier.CLINICIAN_PRO) && !isAppleCompliant && (
        <PricingPlanCard
          tier={SubscriptionTier.CLINICIAN_PRO}
          period={period}
          product={clinicianProduct}
          isCurrentPlan={currentPlan === SubscriptionTier.CLINICIAN_PRO}
          isMostPopular={visiblePlans.length === 3}
          features={
            previousStatusBeforeClinicianTrial === SubscriptionStatus.PRO
              ? clinicianEvidenceFeatures
              : clinicianFeatures
          }
          featuresSectionTitle={
            previousStatusBeforeClinicianTrial === SubscriptionStatus.PRO
              ? 'pricingPlansModal.features.everythingInPro'
              : 'pricingPlansModal.features.everythingInFree'
          }
          ctaText={
            currentPlan === SubscriptionTier.CLINICIAN_PRO
              ? 'pricingPlansModal.keepClinician'
              : 'pricingPlansModal.getClinician'
          }
          onCtaClick={() => void handleClinicianCheckout()}
          isCtaLoading={isPending}
          isCtaDisabled={shouldDisableUpgradeForNonStripeManagedTeam}
          ctaDisabledTooltip={contactSalesTooltip}
          seeMoreHref={pricingPlansUrl}
        />
      )}
      {visiblePlans.includes(SubscriptionTier.CLINICIAN_PRO_PLUS) &&
        !isAppleCompliant && (
          <PricingPlanCard
            tier={SubscriptionTier.CLINICIAN_PRO_PLUS}
            period={period}
            product={practiceProduct}
            isCurrentPlan={currentPlan === SubscriptionTier.CLINICIAN_PRO_PLUS}
            features={
              previousStatusBeforeClinicianTrial === SubscriptionStatus.PRO_PLUS
                ? practiceNewFeatures
                : practiceFeatures
            }
            featuresSectionTitle={
              previousStatusBeforeClinicianTrial === SubscriptionStatus.PRO_PLUS
                ? 'pricingPlansModal.features.everythingInYourOldPlan'
                : 'pricingPlansModal.features.everythingInClinician'
            }
            ctaText={
              currentPlan === SubscriptionTier.CLINICIAN_PRO_PLUS
                ? 'pricingPlansModal.keepPractice'
                : 'pricingPlansModal.getPractice'
            }
            onCtaClick={() => void handlePracticeCheckout()}
            isCtaLoading={isPending}
            isCtaDisabled={shouldDisableUpgradeForNonStripeManagedTeam}
            ctaDisabledTooltip={contactSalesTooltip}
            seeMoreHref={pricingPlansUrl}
          />
        )}
      {isAppleCompliant && (
        <div className="col-span-full flex w-full justify-center py-4">
          <AppleIapInfoText className="text-center text-text-secondary" />
        </div>
      )}
    </div>
  );
}
